import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PlatformMap } from "@/components/PlatformMap";
import { HeartRateZoneChart } from "@/components/HeartRateZoneChart";
import { RouteHrEffortChart } from "@/components/RouteHrEffortChart";
import { buildZoneColoredRouteSegments } from "@/lib/routeHrZones";
import type { SummaryHeartRate } from "@/components/RunSummaryCarousels";
import { SAFE_AREA } from "@/utils/safeArea";

type Props = {
  showExpandedMap: boolean;
  showExpandedHr: boolean;
  showExpandedCombined: boolean;
  onCloseMap: () => void;
  onCloseHr: () => void;
  onCloseCombined: () => void;
  title: string;
  subtitle: string;
  routeLine: [number, number][];
  routeSegments: [number, number][][];
  coloredRouteSegments: ReturnType<typeof buildZoneColoredRouteSegments>;
  routePoints: Array<{ lat: number; lng: number; timestamp?: number }>;
  heartRate: SummaryHeartRate | null;
  closeMapAria: string;
  closeHrAria: string;
};

export function RunSummaryExpandedOverlays({
  showExpandedMap,
  showExpandedHr,
  showExpandedCombined,
  onCloseMap,
  onCloseHr,
  onCloseCombined,
  title,
  subtitle,
  routeLine,
  routeSegments,
  coloredRouteSegments,
  routePoints,
  heartRate,
  closeMapAria,
  closeHrAria,
}: Props) {
  const mapCenter =
    routeLine.length > 0 ? routeLine[Math.floor(routeLine.length / 2)] : null;

  return (
    <>
      {showExpandedCombined &&
        createPortal(
          <div
            className="fixed inset-0 z-[100002] flex flex-col bg-background"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              paddingTop: SAFE_AREA.top,
              paddingBottom: SAFE_AREA.bottom,
              paddingLeft: SAFE_AREA.left,
              paddingRight: SAFE_AREA.right,
            }}
          >
            <div className="shrink-0 flex items-center justify-between py-3 px-2">
              <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
                <div className="text-sm font-semibold">{title}</div>
                <div className="text-xs text-muted-foreground">{subtitle} • Route + HR</div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onCloseCombined}
                className="min-w-[44px] min-h-[44px] shrink-0 touch-manipulation"
                aria-label={closeMapAria}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 min-h-0 flex flex-col gap-3 px-2 pb-2">
              {routeLine.length > 1 && (
                <div className="flex-1 min-h-[40%] rounded-lg overflow-hidden border border-border/30">
                  <PlatformMap
                    key="expanded-combined-map"
                    center={mapCenter}
                    routeSegments={coloredRouteSegments.length > 0 ? undefined : routeSegments}
                    coloredRouteSegments={coloredRouteSegments.length > 0 ? coloredRouteSegments : undefined}
                    showUserMarker={false}
                    showRoute
                    showStartFinishMarkers
                    showKmMarkers
                    fitToRoute
                    className="w-full h-full"
                    darkMode
                    interactive
                    zoom={14}
                  />
                </div>
              )}
              {heartRate ? (
                <div className="shrink-0 space-y-3 rounded-lg border border-border/30 bg-muted/10 p-3 overflow-y-auto max-h-[45%]">
                  <RouteHrEffortChart
                    routePoints={routePoints}
                    hrSamples={heartRate.samples ?? []}
                    zones={heartRate.zones}
                  />
                  <HeartRateZoneChart
                    zones={heartRate.zones}
                    buckets={heartRate.buckets}
                    maxHr={heartRate.maxHr}
                    maxHrSource={heartRate.maxHrSource}
                    avgBpm={heartRate.avgBpm}
                    peakBpm={heartRate.peakBpm}
                    targetZone={heartRate.targetZone}
                  />
                </div>
              ) : null}
            </div>
          </div>,
          document.body,
        )}

      {showExpandedHr && heartRate &&
        createPortal(
          <div
            className="fixed inset-0 z-[100002] flex flex-col bg-background"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              paddingTop: SAFE_AREA.top,
              paddingBottom: SAFE_AREA.bottom,
              paddingLeft: SAFE_AREA.left,
              paddingRight: SAFE_AREA.right,
            }}
          >
            <div className="shrink-0 flex items-center justify-between py-3 px-2">
              <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
                <div className="text-sm font-semibold">Heart rate zones</div>
                <div className="text-xs text-muted-foreground">
                  Avg {heartRate.avgBpm ?? "—"} bpm • Peak {heartRate.peakBpm ?? "—"} bpm
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onCloseHr}
                className="min-w-[44px] min-h-[44px] shrink-0 touch-manipulation"
                aria-label={closeHrAria}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2">
              <HeartRateZoneChart
                zones={heartRate.zones}
                buckets={heartRate.buckets}
                maxHr={heartRate.maxHr}
                maxHrSource={heartRate.maxHrSource}
                avgBpm={heartRate.avgBpm}
                peakBpm={heartRate.peakBpm}
                targetZone={heartRate.targetZone}
              />
            </div>
          </div>,
          document.body,
        )}

      {showExpandedMap &&
        createPortal(
          <div
            className="fixed inset-0 z-[100002] flex flex-col bg-background"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              paddingTop: SAFE_AREA.top,
              paddingBottom: SAFE_AREA.bottom,
              paddingLeft: SAFE_AREA.left,
              paddingRight: SAFE_AREA.right,
            }}
          >
            <div className="shrink-0 flex items-center justify-between py-3 px-2">
              <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
                <div className="text-sm font-semibold">{title}</div>
                <div className="text-xs text-muted-foreground">{subtitle}</div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onCloseMap}
                className="min-w-[44px] min-h-[44px] shrink-0 touch-manipulation"
                aria-label={closeMapAria}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 min-h-0 px-2 pb-2">
              {routeLine.length > 1 ? (
                <div className="w-full h-full rounded-lg overflow-hidden border border-border/30">
                  <PlatformMap
                    key="expanded-map"
                    center={mapCenter}
                    routePoints={routeLine}
                    routeSegments={routeSegments}
                    showUserMarker={false}
                    showRoute
                    showStartFinishMarkers
                    showKmMarkers
                    fitToRoute
                    fitPaddingBottom={1.25}
                    className="w-full h-full"
                    darkMode
                    interactive
                    zoom={14}
                  />
                </div>
              ) : null}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
